import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

const OnlineBooking = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    citizenName: '',
    phoneNumber: '',
    lineId: '',
    permitType: '',
    firearmSubType: '',
    appointmentDate: '',
    timeSlot: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
   const [showSuccessModal, setShowSuccessModal] = useState(false);
   const [showConfirmModal, setShowConfirmModal] = useState(false);
   const [pdpaConsent, setPdpaConsent] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('booking'); // 'booking' or 'search'
  const [searchForm, setSearchForm] = useState({
    bookingNumber: '',
    phoneNumber: '',
    citizenName: ''
  });
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResult, setShowSearchResult] = useState(false);

  const permitTypes = [
    { value: 'firearm', label: 'อาวุธปืน' },
    { value: 'hotel', label: 'โรงแรม' },
    { value: 'gambling', label: 'การพนัน' },
    { value: 'service', label: 'สถานบริการ' },
    { value: 'antique', label: 'ค้าของเก่า' }
  ];

  const firearmSubTypes = [
    { value: 'p3_purchase_permit', label: 'การขออนุญาตมีและใช้อาวุธปืนและเครื่องกระสุนปืน (ออกใบอนุญาตซื้อ ป.3)' },
    { value: 'p4_possession_permit', label: 'การออกใบอนุญาตให้มีและใช้อาวุธปืนและเครื่องกระสุนปืน (ป.4)' },
    { value: 'p4_transfer_endorse', label: 'การขอสลักหลังตัดโอนใบอนุญาตให้มีและใช้อาวุธปืนและเครื่องกระสุนปืน (ป.4)' },
    { value: 'other', label: 'อื่นๆ' }
  ];

  const timeSlots = [
    { value: '09:00-10:00', label: '09:00 - 10:00' },
    { value: '10:00-11:00', label: '10:00 - 11:00' },
    { value: '11:00-12:00', label: '11:00 - 12:00' },
    { value: '13:00-14:00', label: '13:00 - 14:00' },
    { value: '14:00-15:00', label: '14:00 - 15:00' }
  ];

  // ดึงข้อมูลเวลาที่ถูกจองแล้วในวันที่เลือก
  const fetchBookedSlots = async (date) => {
    if (!date) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('time_slot')
        .eq('appointment_date', date)
        .eq('status', 'confirmed');

      if (error) {
        console.error('Error fetching booked slots:', error);
        return;
      }

      setBookedSlots(data.map(item => item.time_slot));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // เมื่อเปลี่ยนวันที่ ให้ดึงข้อมูลเวลาที่จองแล้ว
  useEffect(() => {
    if (formData.appointmentDate) {
      fetchBookedSlots(formData.appointmentDate);
    }
  }, [formData.appointmentDate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.citizenName.trim()) {
      newErrors.citizenName = 'กรุณากรอกชื่อ-สกุล';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'กรุณากรอกหมายเลขโทรศัพท์';
    } else if (!/^[0-9]{9,10}$/.test(formData.phoneNumber.replace(/[-\s]/g, ''))) {
      newErrors.phoneNumber = 'หมายเลขโทรศัพท์ไม่ถูกต้อง (9-10 หลัก)';
    }

    if (!formData.lineId.trim()) {
      newErrors.lineId = 'กรุณากรอกไอดีไลน์';
    }

    if (!formData.permitType) {
      newErrors.permitType = 'กรุณาเลือกประเภทการขออนุญาต';
    }

    if (formData.permitType === 'firearm' && !formData.firearmSubType) {
      newErrors.permitType = 'กรุณาเลือกหัวข้อย่อยของอาวุธปืน';
    }

    if (!formData.appointmentDate) {
      newErrors.appointmentDate = 'กรุณาเลือกวันที่';
    }

    if (!formData.timeSlot) {
      newErrors.timeSlot = 'กรุณาเลือกช่วงเวลา';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value || ''
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const generateBookingNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `APT${year}${month}${day}${random}`;
  };

  // ส่งการแจ้งเตือนผ่าน LINE Notify (ใช้ระบบเดียวกับเรื่องร้องเรียน)
  const sendLineNotification = async (bookingData) => {
    try {
      await supabase.functions.invoke('line-notify', {
        body: {
          type: 'appointment_booking',
          booking_number: bookingData.booking_number,
          citizen_name: bookingData.citizen_name,
          phone_number: bookingData.phone_number,
          permit_type: getPermitTypeLabel(bookingData.permit_type),
          appointment_date: new Date(bookingData.appointment_date).toLocaleDateString('th-TH'),
          time_slot: bookingData.time_slot
        }
      });
    } catch (error) {
      console.error('LINE Notify error:', error);
      // Don't fail the booking if LINE fails
    }
  };

  const getPermitTypeLabel = (type) => {
    const permit = permitTypes.find(p => p.value === type);
    return permit ? permit.label : type;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    // Show confirmation modal instead of submitting directly
    setShowConfirmModal(true);
  };
  
   const confirmSubmission = async () => {
     // Require PDPA consent before submission
     if (!pdpaConsent) return;
     setShowConfirmModal(false);
     setIsSubmitting(true);
     try {
      const bookingNumber = generateBookingNumber();
      
      // Debug: Log the data being sent
      console.log('Sending data:', {
        booking_number: bookingNumber,
        citizen_name: formData.citizenName.trim(),
        citizen_id: 'N/A',
        phone_number: formData.phoneNumber.trim(),
        line_id: formData.lineId.trim(),
        permit_type: formData.permitType,
        firearm_sub_type: formData.firearmSubType || null,
        appointment_date: formData.appointmentDate,
        time_slot: formData.timeSlot,
        status: 'confirmed',
        created_at: new Date().toISOString()
      });
      
      const { data, error } = await supabase
        .from('appointments')
        .insert({
          booking_number: bookingNumber,
          citizen_name: formData.citizenName.trim(),
          citizen_id: 'N/A', // Default value since we removed the field
          phone_number: formData.phoneNumber.trim(),
          line_id: formData.lineId.trim(),
          permit_type: formData.permitType,
          firearm_sub_type: formData.firearmSubType || null,
          appointment_date: formData.appointmentDate,
          time_slot: formData.timeSlot,
          status: 'confirmed',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw new Error(`เกิดข้อผิดพลาดในการจองคิว: ${error.message}`);
      }

      // ส่งการแจ้งเตือนผ่าน LINE Notify
      await sendLineNotification(data);

      setBookingResult({
        bookingNumber: data.booking_number,
        citizenName: data.citizen_name,
        permitType: getPermitTypeLabel(data.permit_type),
        appointmentDate: new Date(data.appointment_date).toLocaleDateString('th-TH'),
        timeSlot: data.time_slot
      });

      setShowSuccessModal(true);
    } catch (error) {
      console.error('Submission error:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      citizenName: '',
      phoneNumber: '',
      lineId: '',
      permitType: '',
      firearmSubType: '',
      appointmentDate: '',
      timeSlot: ''
    });
    setErrors({});
    setBookedSlots([]);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    resetForm();
  };

  // ฟังก์ชันค้นหาคิว
  const handleSearch = async () => {
    if (!searchForm.bookingNumber.trim() && !searchForm.phoneNumber.trim() && !searchForm.citizenName.trim()) {
      alert('กรุณากรอกข้อมูลอย่างน้อย 1 ช่อง');
      return;
    }

    setSearchLoading(true);
    setSearchResult(null);
    setShowSearchResult(false);

    try {
      let query = supabase
        .from('appointments')
        .select('*');

      // ถ้ามีเลขที่จอง ให้ค้นหาจากเลขที่จอง
      if (searchForm.bookingNumber.trim()) {
        query = query.eq('booking_number', searchForm.bookingNumber.trim().toUpperCase());
      } else {
        // ถ้าไม่มีเลขที่จอง ให้ค้นหาจากชื่อและเบอร์โทรศัพท์
        const conditions = [];
        if (searchForm.citizenName.trim()) {
          conditions.push(`citizen_name.ilike.%${searchForm.citizenName.trim()}%`);
        }
        if (searchForm.phoneNumber.trim()) {
          conditions.push(`phone_number.ilike.%${searchForm.phoneNumber.trim()}%`);
        }
        if (conditions.length > 0) {
          query = query.or(conditions.join(','));
        }
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Search error:', error);
        throw new Error('เกิดข้อผิดพลาดในการค้นหา');
      }

      if (!data || data.length === 0) {
        setSearchResult(null);
        setShowSearchResult(true);
        alert('ไม่พบข้อมูลการจองคิว');
      } else {
        // ถ้ามีหลายรายการ ให้แสดงรายการแรก หรือแสดงทั้งหมด
        const result = data.length === 1 ? data[0] : data;
        setSearchResult(result);
        setShowSearchResult(true);
      }
    } catch (error) {
      console.error('Search error:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'confirmed': 'ยืนยันแล้ว',
      'cancelled': 'ยกเลิก',
      'completed': 'เสร็จสิ้น',
      'pending': 'รอดำเนินการ'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'confirmed': 'bg-blue-100 text-blue-700',
      'cancelled': 'bg-red-100 text-red-700',
      'completed': 'bg-green-100 text-green-700',
      'pending': 'bg-yellow-100 text-yellow-700'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 lg:px-6 py-6">
        <BreadcrumbTrail />
        
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={24} color="white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                จองคิวออนไลน์
              </h1>
              <p className="text-muted-foreground mt-1">
                ระบบจองคิวออนไลน์สำหรับงานขออนุญาตฝ่ายปกครอง
              </p>
            </div>
          </div>
          
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-accent-foreground">
                  <strong>หมายเหตุ:</strong> กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง 
                  และเลือกวันที่และเวลาที่สะดวกในการเข้ารับบริการ
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row gap-2 bg-muted/30 rounded-xl p-2">
            <button
              onClick={() => setActiveTab('booking')}
              className={`flex items-center space-x-3 px-6 py-4 rounded-lg transition-all duration-200 hover:shadow-md active:scale-[0.98] ${
                activeTab === 'booking'
                  ? 'bg-white text-gray-800 shadow-sm border border-gray-200'
                  : 'bg-transparent text-gray-600 hover:bg-white/50'
              }`}
            >
              <Icon name="Calendar" size={20} className="text-blue-600" />
              <span className="font-medium">จองคิวออนไลน์</span>
            </button>
            
            <button
              onClick={() => setActiveTab('search')}
              className={`flex items-center space-x-3 px-6 py-4 rounded-lg transition-all duration-200 hover:shadow-md active:scale-[0.98] ${
                activeTab === 'search'
                  ? 'bg-white text-gray-800 shadow-sm border border-gray-200'
                  : 'bg-transparent text-gray-600 hover:bg-white/50'
              }`}
            >
              <Icon name="Search" size={20} className="text-blue-600" />
              <span className="font-medium">ค้นหาคิวของฉัน</span>
            </button>
            
            <button
              onClick={() => navigate('/appointment-calendar')}
              className="flex items-center space-x-3 px-6 py-4 rounded-lg bg-transparent text-gray-600 hover:bg-white/50 transition-all duration-200 hover:shadow-md active:scale-[0.98]"
            >
              <Icon name="Calendar" size={20} className="text-blue-600" />
              <span className="font-medium">ดูปฏิทินนัดหมาย</span>
            </button>
          </div>
        </div>


        {/* Search Form */}
        {activeTab === 'search' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-8 mb-8">
            <div className="flex items-center space-x-4 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                <Icon name="Search" size={24} color="white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  ค้นหาคิวของฉัน
                </h2>
                <p className="text-gray-600 mt-1">
                  กรุณากรอกข้อมูลเพื่อค้นหาการจองคิวของคุณ
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-blue-800">
                      <strong>วิธีค้นหา:</strong> คุณสามารถค้นหาได้โดยใช้เลขที่จอง หรือใช้ชื่อ-นามสกุลพร้อมเบอร์โทรศัพท์
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="เลขที่จอง"
                  type="text"
                  placeholder="APTYYYYMMDD###"
                  value={searchForm.bookingNumber}
                  onChange={(e) => setSearchForm(prev => ({ ...prev, bookingNumber: e.target.value }))}
                />
                <Input
                  label="ชื่อ-นามสกุล"
                  type="text"
                  placeholder="กรอกชื่อ-นามสกุล"
                  value={searchForm.citizenName}
                  onChange={(e) => setSearchForm(prev => ({ ...prev, citizenName: e.target.value }))}
                />
                <Input
                  label="หมายเลขโทรศัพท์"
                  type="tel"
                  placeholder="08X-XXX-XXXX"
                  value={searchForm.phoneNumber}
                  onChange={(e) => setSearchForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSearch}
                  disabled={searchLoading}
                  className="px-8 py-3"
                >
                  {searchLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      <span>กำลังค้นหา...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="Search" size={18} className="mr-2" />
                      <span>ค้นหา</span>
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Search Results */}
            {showSearchResult && searchResult && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-xl font-bold text-gray-800 mb-6">ผลการค้นหา</h3>
                {Array.isArray(searchResult) ? (
                  <div className="space-y-4">
                    {searchResult.map((appointment, index) => (
                      <div key={appointment.id || index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">เลขที่จอง:</p>
                            <p className="text-lg font-bold text-blue-600">{appointment.booking_number}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">สถานะ:</p>
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(appointment.status)}`}>
                              {getStatusLabel(appointment.status)}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">ชื่อ-นามสกุล:</p>
                            <p className="text-base font-medium text-gray-800">{appointment.citizen_name}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">เบอร์โทรศัพท์:</p>
                            <p className="text-base font-medium text-gray-800">{appointment.phone_number}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">ประเภทการขออนุญาต:</p>
                            <p className="text-base font-medium text-gray-800">{getPermitTypeLabel(appointment.permit_type)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">วันที่นัดหมาย:</p>
                            <p className="text-base font-medium text-gray-800">
                              {new Date(appointment.appointment_date).toLocaleDateString('th-TH', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">ช่วงเวลา:</p>
                            <p className="text-base font-medium text-gray-800">{appointment.time_slot}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">วันที่จอง:</p>
                            <p className="text-base font-medium text-gray-800">
                              {new Date(appointment.created_at).toLocaleDateString('th-TH', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">เลขที่จอง:</p>
                        <p className="text-lg font-bold text-blue-600">{searchResult.booking_number}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">สถานะ:</p>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(searchResult.status)}`}>
                          {getStatusLabel(searchResult.status)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">ชื่อ-นามสกุล:</p>
                        <p className="text-base font-medium text-gray-800">{searchResult.citizen_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">เบอร์โทรศัพท์:</p>
                        <p className="text-base font-medium text-gray-800">{searchResult.phone_number}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">ประเภทการขออนุญาต:</p>
                        <p className="text-base font-medium text-gray-800">{getPermitTypeLabel(searchResult.permit_type)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">วันที่นัดหมาย:</p>
                        <p className="text-base font-medium text-gray-800">
                          {new Date(searchResult.appointment_date).toLocaleDateString('th-TH', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">ช่วงเวลา:</p>
                        <p className="text-base font-medium text-gray-800">{searchResult.time_slot}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">วันที่จอง:</p>
                        <p className="text-base font-medium text-gray-800">
                          {new Date(searchResult.created_at).toLocaleDateString('th-TH', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Booking Form */}
        {activeTab === 'booking' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-8">
          <div className="flex items-center space-x-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <Icon name="User" size={24} color="white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                ข้อมูลผู้จองคิว
              </h2>
              <p className="text-gray-600 mt-1">
                กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="ชื่อ-สกุล"
                type="text"
                placeholder="กรอกชื่อ-สกุลของท่าน"
                value={formData.citizenName}
                onChange={(e) => handleInputChange('citizenName', e.target.value)}
                error={errors.citizenName}
                required
              />

              <Input
                label="หมายเลขโทรศัพท์"
                type="tel"
                placeholder="08X-XXX-XXXX"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                error={errors.phoneNumber}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="ไอดีไลน์"
                type="text"
                placeholder="กรอกไอดีไลน์ของท่าน"
                value={formData.lineId}
                onChange={(e) => handleInputChange('lineId', e.target.value)}
                error={errors.lineId}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="ประเภทการขออนุญาต"
                placeholder="เลือกประเภทการขออนุญาต"
                options={permitTypes}
                value={formData.permitType}
                onChange={(val) => handleInputChange('permitType', val)}
                error={errors.permitType}
                required
              />
              {formData.permitType === 'firearm' && (
                <Select
                  label="หัวข้อย่อย (อาวุธปืน)"
                  placeholder="เลือกรายการ"
                  options={firearmSubTypes}
                  value={formData.firearmSubType}
                  onChange={(val) => handleInputChange('firearmSubType', val)}
                  required
                />
              )}
            </div>

            {/* Appointment Details */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={16} color="white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  รายละเอียดการนัดหมาย
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    วันที่นัดหมาย
                    <span className="text-error ml-1">*</span>
                  </label>
                  <input
                    type="date"
                    className={`w-full rounded-md border bg-background px-3 py-2 text-sm outline-none transition-colors ${
                      errors.appointmentDate ? 'border-error focus:border-error' : 'border-border focus:border-primary/60'
                    }`}
                    value={formData.appointmentDate}
                    onChange={(e) => handleInputChange('appointmentDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                  {errors.appointmentDate && (
                    <p className="text-xs text-error mt-1">{errors.appointmentDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    ช่วงเวลา
                    <span className="text-error ml-1">*</span>
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {timeSlots.map((slot) => {
                      const isBooked = bookedSlots.includes(slot.value);
                      const isSelected = formData.timeSlot === slot.value;
                      
                      return (
                        <button
                          key={slot.value}
                          type="button"
                          disabled={isBooked}
                          onClick={() => handleInputChange('timeSlot', slot.value)}
                          className={`p-3 rounded-md border text-sm transition-colors ${
                            isBooked
                              ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                              : isSelected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-background hover:border-primary/60'
                          }`}
                        >
                          {slot.label}
                          {isBooked && <span className="ml-2 text-xs">(จองแล้ว)</span>}
                        </button>
                      );
                    })}
                  </div>
                  {errors.timeSlot && (
                    <p className="text-xs text-error mt-1">{errors.timeSlot}</p>
                  )}
                </div>
              </div>

              {loading && (
                <div className="text-center py-4">
                  <div className="inline-flex items-center space-x-2 text-muted-foreground">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <span>กำลังตรวจสอบเวลาที่ว่าง...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-gray-200">
              <button
                type="button"
                onClick={resetForm}
                className="flex items-center justify-center space-x-2 px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 active:scale-[0.98] sm:order-1"
              >
                <Icon name="RotateCcw" size={18} />
                <span>ล้างข้อมูล</span>
              </button>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed sm:order-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>กำลังจองคิว...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Calendar" size={18} />
                    <span>จองคิว</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        )}

        {/* Additional Information */}
        <div className="mt-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <img 
                src="/assets/images/b822850b984eba9df2620697399b50075bab49cf64ce35210033003315257900-1757413105446.png" 
                alt="ตราสิงที่ทำการปกครองอำเภอเทิง" 
                className="w-14 h-14 object-contain"
              />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              ข้อมูลการให้บริการ
            </h3>
            <p className="text-gray-600 text-base leading-relaxed max-w-2xl mx-auto">
              ระบบจองคิวออนไลน์นี้ให้บริการสำหรับการขออนุญาตต่างๆ ของฝ่ายปกครอง 
              เพื่อความสะดวกและลดเวลารอคอยของประชาชน
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Clock" size={24} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">ประหยัดเวลา</h4>
                <p className="text-sm text-gray-600">ไม่ต้องรอคิวยาวนาน</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Smartphone" size={24} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">สะดวกสบาย</h4>
                <p className="text-sm text-gray-600">จองได้ทุกที่ทุกเวลา</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Shield" size={24} className="text-purple-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">ปลอดภัย</h4>
                <p className="text-sm text-gray-600">ข้อมูลได้รับการปกป้อง</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1">
          <div className="bg-white rounded-lg shadow-md max-w-[90vw] sm:max-w-[80vw] md:max-w-[60vw] lg:max-w-[45vw] xl:max-w-[35vw] max-h-[85vh] overflow-y-auto">
            <div className="p-2 sm:p-3">
              <div className="text-center mb-2">
                <div className="mx-auto flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full mb-1">
                  <AlertTriangle className="text-blue-600" size={12} />
                </div>
                <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-0.5">
                  ยืนยันการจองคิวและการเผยแพร่ข้อมูล
                </h3>
                <p className="text-[9px] sm:text-[10px] text-gray-600">
                  กรุณาตรวจสอบข้อมูลของท่านให้ถูกต้องก่อนยืนยันการจอง
                </p>
              </div>

              {/* Booking Information */}
              <div className="bg-gray-50 rounded-md p-1.5 space-y-0.5 border border-gray-100">
                <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
                  <span className="text-[9px] sm:text-[10px] text-gray-600">ชื่อ-นามสกุล:</span>
                  <span className="text-[9px] sm:text-[10px] font-medium text-gray-900">{formData.citizenName}</span>
                </div>
                <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
                  <span className="text-[9px] sm:text-[10px] text-gray-600">หมายเลขโทรศัพท์:</span>
                  <span className="text-[9px] sm:text-[10px] font-medium text-gray-900">{formData.phoneNumber}</span>
                </div>
                <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
                  <span className="text-[9px] sm:text-[10px] text-gray-600">ไอดีไลน์:</span>
                  <span className="text-[9px] sm:text-[10px] font-medium text-gray-900">{formData.lineId}</span>
                </div>
                <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
                  <span className="text-[9px] sm:text-[10px] text-gray-600">ประเภท:</span>
                  <span className="text-[9px] sm:text-[10px] font-medium text-gray-900">{getPermitTypeLabel(formData.permitType)}</span>
                </div>
                <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
                  <span className="text-[9px] sm:text-[10px] text-gray-600">วันที่:</span>
                  <span className="text-[9px] sm:text-[10px] font-medium text-gray-900">{formData.appointmentDate}</span>
                </div>
                <div className="flex justify-between items-center py-0.5">
                  <span className="text-[9px] sm:text-[10px] text-gray-600">เวลา:</span>
                  <span className="text-[9px] sm:text-[10px] font-medium text-gray-900">{formData.timeSlot}</span>
                </div>
              </div>

              {/* PDPA Consent */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-1 sm:p-1.5 mb-1.5">
                <p className="text-[8px] sm:text-[9px] text-yellow-800 mb-1">
                  <strong>การให้ความยินยอมตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล (PDPA):</strong>
                </p>
                <ul className="text-[8px] sm:text-[9px] text-yellow-700 space-y-0.5 list-disc list-inside">
                    <li>ข้าพเจ้ายินยอมให้หน่วยงานเก็บรวบรวม ใช้ และเปิดเผยข้อมูลส่วนบุคคลเพื่อการให้บริการ</li>
                    <li>ข้อมูลจะถูกใช้เพื่อการติดต่อ การให้บริการ และการปรับปรุงคุณภาพการบริการ</li>
                    <li>ท่านสามารถขอเข้าถึง แก้ไข หรือลบข้อมูลได้ตามสิทธิที่กฎหมายกำหนด</li>
                </ul>
                <div className="flex items-center mt-1">
                  <input
                    type="checkbox"
                    id="pdpa-consent"
                    checked={pdpaConsent}
                    onChange={(e) => setPdpaConsent(e.target.checked)}
                    className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="pdpa-consent" className="ml-1 text-[8px] sm:text-[9px] text-yellow-800">
                    ข้าพเจ้ายินยอมตามเงื่อนไขข้างต้น
                  </label>
                </div>
              </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 pt-1">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="flex-1 px-2 py-1 text-[9px] sm:text-[10px] text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                  >
                    ยกเลิก
                  </button>
                  <button
                    onClick={confirmSubmission}
                    disabled={isSubmitting || !pdpaConsent}
                    className="flex-1 px-2 py-1 text-[9px] sm:text-[10px] text-white bg-blue-500 rounded hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  >
                    {isSubmitting ? 'กำลังจอง...' : 'ยืนยันการจอง'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      
      {/* Success Modal */}
      {showSuccessModal && bookingResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-1">
          <div className="bg-white rounded-lg shadow-md max-w-[40vw] sm:max-w-[200px] w-full transform transition-all duration-300 scale-100 max-h-[70vh] overflow-y-auto">
            <div className="p-2">
              <div className="text-center mb-2">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1">
                  <Icon name="CheckCircle" size={12} color="#10b981" />
                </div>
                <h3 className="text-xs font-semibold text-gray-800 mb-0.5">
                  จองคิวสำเร็จ!
                </h3>
                <p className="text-gray-600 text-[10px]">
                  การจองคิวของคุณได้รับการยืนยันแล้ว
                </p>
              </div>

              <div className="bg-gray-50 rounded-md p-1.5 space-y-0.5 border border-gray-100">
                <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
                  <span className="text-[10px] font-medium text-gray-700">เลขที่จอง:</span>
                  <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1 py-0.5 rounded">{bookingResult.bookingNumber}</span>
                </div>
                <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
                  <span className="text-[10px] font-medium text-gray-700">ชื่อ-สกุล:</span>
                  <span className="text-[10px] font-medium text-gray-800">{bookingResult.citizenName}</span>
                </div>
                <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
                  <span className="text-[10px] font-medium text-gray-700">ประเภท:</span>
                  <span className="text-[10px] font-medium text-gray-800">{bookingResult.permitType}</span>
                </div>
                <div className="flex justify-between items-center py-0.5 border-b border-gray-100">
                  <span className="text-[10px] font-medium text-gray-700">วันที่:</span>
                  <span className="text-[10px] font-medium text-gray-800">{bookingResult.appointmentDate}</span>
                </div>
                <div className="flex justify-between items-center py-0.5">
                  <span className="text-[10px] font-medium text-gray-700">เวลา:</span>
                  <span className="text-[10px] font-medium text-gray-800">{bookingResult.timeSlot}</span>
                </div>
              </div>

              <div className="flex justify-center pt-1.5">
                <button
                  onClick={handleSuccessModalClose}
                  className="px-2 py-0.5 bg-blue-500 text-white text-[10px] rounded hover:bg-blue-600 active:scale-[0.98]"
                >
                  ปิด
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineBooking;
